import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import {
  CurrencyService,
  ICurrency,
} from '@app/core/services/currency.service';
import {
  CurrencyExchangeRate,
  CurrencyExchangeRateGQL,
  CurrencyExchangeRateQuery,
  CurrencyExchangeRateQueryVariables,
  DeleteCurrencyExchangeRateGQL,
  DeleteCurrencyExchangeRateMutation,
  DeleteCurrencyExchangeRateMutationVariables,
  ExchangeableTypeEnum,
  ExchangeRateTypeEnum,
  UpsertCurrencyExchangeRateGQL,
  UpsertCurrencyExchangeRateMutation,
  UpsertCurrencyExchangeRateMutationVariables,
} from '@app/generated/graphql';
import { Apollo } from 'apollo-angular';
import { map } from 'rxjs/operators';

export interface IExchangeRate {
  id?: string; // won't have if new currency exchange rate
  currencyConversionId?: string; // won't have if custom rate
  exchangeRate: string; // eg. '1.000000'
  exchangeRateType: ExchangeRateTypeEnum; // eg. 'Fixed', 'Custom', 'Spot rate'
  exchangeRateDate: Date; // eg. '2021-01-01'
  fromCurrency: ICurrency; // eg. { currencyCode: 'USD', currencySymbol: '$', currencyName: 'United States Dollar', countryCode: 'US', label: '🇺🇸 USD' }
  toCurrency: ICurrency; // eg. { currencyCode: 'ZAR', currencySymbol: 'R', currencyName: 'South African Rand', countryCode: 'ZA', label: '🇿🇦 ZAR' }
  exchangeableId: string; // eg. '123'
  exchangeableType: ExchangeableTypeEnum; // eg. 'Invoice' or 'Payment'
}

// used to manage the state of the exchange rate picker component
@Injectable()
export class Ag1ExchangeRatePickerService {
  private apollo = inject(Apollo);
  private currencyExchangeRateGql = inject(CurrencyExchangeRateGQL);
  private upsertCurrencyExchangeRateGql = inject(UpsertCurrencyExchangeRateGQL);
  private deleteCurrencyExchangeRateGql = inject(DeleteCurrencyExchangeRateGQL);
  private currencyService = inject(CurrencyService);

  private _stateSubject: BehaviorSubject<IExchangeRate> =
    new BehaviorSubject<IExchangeRate>(null);

  state$ = this._stateSubject.asObservable();

  constructor() {}

  public updateState(state: IExchangeRate): void {
    const clonedState: IExchangeRate = structuredClone(state);
    this._stateSubject.next(clonedState);
  }

  public getState(): IExchangeRate {
    return this._stateSubject.getValue();
  }

  public deleteCurrencyExchangeRate(
    currencyExchangeRateId: string,
  ): Observable<DeleteCurrencyExchangeRateMutation> {
    const variables: DeleteCurrencyExchangeRateMutationVariables = {
      currencyExchangeRateId: currencyExchangeRateId,
    };

    return this.apollo
      .mutate<DeleteCurrencyExchangeRateMutation>({
        mutation: this.deleteCurrencyExchangeRateGql.document,
        variables: variables,
      })
      .pipe(map((res) => res.data as DeleteCurrencyExchangeRateMutation));
  }

  public setCurrencyExchangeRate(
    exchangeRate: IExchangeRate,
  ): Observable<CurrencyExchangeRate> {
    const variables: UpsertCurrencyExchangeRateMutationVariables = {
      currencyExchangeRateAttributes: {
        id: exchangeRate.id || null, // won't have if new currency exchange rate
        currencyConversionId: exchangeRate.currencyConversionId || null, // won't have if custom rate
        exchangeRate: exchangeRate.exchangeRate, // eg. '1.000000'
        exchangeRateType: exchangeRate.exchangeRateType, // eg. 'Fixed', 'Custom', 'Spot rate'
        exchangeRateDate: exchangeRate.exchangeRateDate as unknown as string, // eg. '2021-01-01'
        fromCurrency: exchangeRate.fromCurrency.currencyCode, // eg. 'USD'
        toCurrency: exchangeRate.toCurrency.currencyCode, // eg. 'ZAR'
        exchangeableId: exchangeRate.exchangeableId, // eg. '123'
        exchangeableType: exchangeRate.exchangeableType, // eg. 'Invoice' or 'Payment'
      },
    };

    return this.apollo
      .mutate<UpsertCurrencyExchangeRateMutation>({
        mutation: this.upsertCurrencyExchangeRateGql.document,
        variables: variables,
      })
      .pipe(
        map(
          (res) =>
            res.data?.upsertCurrencyExchangeRate
              .currencyExchangeRate as CurrencyExchangeRate,
        ),
      );
  }

  public getCurrencyExchangeRate(
    exchangeableId: string,
    exchangeableType: string,
  ): Observable<IExchangeRate> {
    const variables: CurrencyExchangeRateQueryVariables = {
      exchangeableId, // id of the resource
      exchangeableType, // eg. 'Invoice' or 'Payment'
    };

    return this.apollo
      .query<CurrencyExchangeRateQuery>({
        query: this.currencyExchangeRateGql.document,
        variables: variables,
      })
      .pipe(
        map((res) => res.data?.currencyExchangeRate as CurrencyExchangeRate),
        // convert the currency exchange rate to an exchange rate that can be
        // used in the exchange rate picker component
        map((currencyExchangeRate: CurrencyExchangeRate) => {
          // no currency exchange rate found
          if (!currencyExchangeRate) {
            return null;
          }

          return {
            ...currencyExchangeRate,
            exchangeableId:
              currencyExchangeRate?.exchangeableId || exchangeableId,
            toCurrency: currencyExchangeRate?.toCurrency
              ? this.currencyService.getCurrencyByCode(
                  currencyExchangeRate.toCurrency,
                )
              : null,
            fromCurrency: currencyExchangeRate?.fromCurrency
              ? this.currencyService.getCurrencyByCode(
                  currencyExchangeRate.fromCurrency,
                )
              : null,
            exchangeRateType: currencyExchangeRate?.exchangeRateType
              ? this.getExchangeRateType(currencyExchangeRate.exchangeRateType)
              : null,
            exchangeableType: currencyExchangeRate?.exchangeableType
              ? this.getExchangeableType(currencyExchangeRate.exchangeableType)
              : exchangeableType
                ? this.getExchangeableType(exchangeableType)
                : null,
          } as unknown as IExchangeRate;
        }),
      );
  }

  public getExchangeableType(type: string): ExchangeableTypeEnum {
    return (
      Object.values(ExchangeableTypeEnum).find(
        (t: ExchangeableTypeEnum) =>
          t.toString().toLowerCase() === type?.toLowerCase(),
      ) || null
    );
  }

  public getExchangeRateType(type: string): ExchangeRateTypeEnum {
    return (
      Object.values(ExchangeRateTypeEnum).find(
        (t: ExchangeRateTypeEnum) =>
          t.toString().toLowerCase() === type?.toLowerCase(),
      ) || null
    );
  }
}
